import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FaCheckCircle, FaClock, FaExchangeAlt, FaFolderOpen,
  FaHourglassHalf, FaPaperPlane, FaTimes, FaUser, FaCalendarAlt,
  FaCommentDots, FaHistory, FaSyncAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getApiUrl, getUsersEmail } from '../../authConfig';
import { getValidToken } from '../../utils/msalTokenUtil';
import { getAssigneeDisplayName, getAssigneeAvatarGradient } from '../../utils/assigneeMapping';
import './ActivityTrackingDrawer.css';

const STATUS_OPTIONS = ['Open', 'In Progress', 'Completed'];

const renderStatusBadge = (status) => {
  switch (status) {
    case 'Completed':
      return (
        <span className="activity-status-badge status-completed">
          <FaCheckCircle size={11} /> Completed
        </span>
      );
    case 'In Progress':
      return (
        <span className="activity-status-badge status-in-progress">
          <FaHourglassHalf size={11} /> In Progress
        </span>
      );
    case 'Open':
    default:
      return (
        <span className="activity-status-badge status-open">
          <FaFolderOpen size={11} /> Open
        </span>
      );
  }
};

const formatTimestamp = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (e) {
    return dateStr;
  }
};

const getRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`);
    const diffMs = Date.now() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  } catch (e) {
    return '';
  }
};

const QUICK_COMMAND_TEMPLATES = [
  { label: '🤝 Reassign Task', text: 'Task reassigned to colleague for further processing.' },
  { label: '📞 Client Follow-up', text: 'Followed up with client regarding missing tax documents.' },
  { label: '📄 Documents Received', text: 'Required receipts and tax slips received. Ready for preparation.' },
  { label: '✅ Review Done', text: 'Initial return review completed. Ready for director / client sign-off.' },
];

const ActivityTrackingDrawer = ({
  isOpen,
  onClose,
  item,
  account,
  msalInstance,
  onItemUpdated,
}) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [commentText, setCommentText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('In Progress');
  const [selectedAssignee, setSelectedAssignee] = useState('');

  const commentInputRef = useRef(null);
  const assigneeOptions = getUsersEmail();

  const userEmail = (account?.username || account?.name || '').trim().toLowerCase();
  const userName = account?.name || userEmail.split('@')[0] || 'User';

    const loadActivities = useCallback(async () => {
    if (!item?.id) return;
    setLoading(true);
    try {
      const token = await getValidToken(msalInstance, account);
      const res = await fetch(getApiUrl(`/v1/action-items/${item.id}/activities`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load activity history.');
      const data = await res.json();
      setActivities(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error('Could not load activity history.');
    } finally {
      setLoading(false);
    }
  }, [item?.id, msalInstance, account]);

  
  // Initialize form with current action item values when drawer opens
  useEffect(() => {
    if (isOpen && item) {
      setSelectedStatus(item.status || 'Open');
      setSelectedAssignee(item.assigned_to || userEmail);
      setCommentText('');
      loadActivities();
      setTimeout(() => {
        if (commentInputRef.current) {
          commentInputRef.current.focus();
        }
      }, 200);
    }
  }, [isOpen, item, loadActivities, userEmail]);

  const handleSubmitActivity = async (e) => {
    e.preventDefault();
    if (!item?.id) return;

    const trimmedComment = commentText.trim();
    const hasStatusChange = selectedStatus && selectedStatus !== item.status;
    const hasAssigneeChange = selectedAssignee && selectedAssignee !== item.assigned_to;

    if (!trimmedComment && !hasStatusChange && !hasAssigneeChange) {
      toast.info('Please enter a comment/command or change the status / assignee.');
      return;
    }

    setSubmitting(true);
    try {
      const token = await getValidToken(msalInstance, account);
      const payload = {
        comment: trimmedComment,
        status: selectedStatus,
        assignedTo: selectedAssignee,
        updatedBy: userEmail || userName,
      };

      const res = await fetch(getApiUrl(`/v1/action-items/${item.id}/activities`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to record activity.');
      }

      const resData = await res.json();
      toast.success(
        hasAssigneeChange
          ? `Update recorded & task reassigned to ${getAssigneeDisplayName(selectedAssignee)}!`
          : 'Update posted successfully!'
      );

      setCommentText('');
      if (resData.activities) {
        setActivities(resData.activities);
      } else {
        loadActivities();
      }

      // Notify parent to refresh due items list
      if (onItemUpdated && resData.item) {
        onItemUpdated(resData.item);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Error saving activity.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !item) return null;

  const isPastDue = item.due_date && item.due_date < new Date().toISOString().split('T')[0];
  const isDueToday = item.due_date && item.due_date === new Date().toISOString().split('T')[0];

  return (
    <>
      {/* Backdrop */}
      <div className="activity-drawer-backdrop show" onClick={onClose} />

      {/* Slide-out Drawer */}
      <div className="activity-drawer" role="dialog" aria-modal="true">
        {/* Drawer Header */}
        <div className="activity-drawer-header">
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center gap-2">
              <div className="activity-drawer-icon-wrap">
                <FaCommentDots size={18} />
              </div>
              <div>
                <h5 className="mb-0 fw-bold activity-drawer-title">
                  Day-to-Day Activity Tracking
                </h5>
                <span className="text-muted small">
                  Activity Feed &amp; Task Updates
                </span>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-close-custom"
              onClick={onClose}
              aria-label="Close activity tracking drawer"
            >
              <FaTimes size={16} />
            </button>
          </div>

          {/* Action Item Target Summary Card */}
          <div className="action-target-summary-card mt-3">
            <div className="d-flex align-items-start justify-content-between gap-2 flex-wrap">
              <div>
                <span className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle me-2" style={{ fontSize: '0.68rem' }}>
                  {item.customer_type || 'Customer'}
                </span>
                <span className="fw-bold text-dark fs-6">
                  {item.customer_name || 'Customer Action Item'}
                </span>
              </div>
              <div className="d-flex align-items-center gap-1">
                {renderStatusBadge(item.status)}
              </div>
            </div>

            <div className="d-flex align-items-center gap-3 mt-2 pt-2 border-top small text-muted flex-wrap">
              <div className="d-flex align-items-center gap-1">
                <FaCalendarAlt size={12} className={isPastDue ? 'text-danger' : isDueToday ? 'text-warning' : 'text-primary'} />
                <span>Due Date: <strong>{item.due_date}</strong></span>
                {isPastDue && <span className="badge bg-danger text-white ms-1" style={{ fontSize: '0.62rem' }}>Past Due</span>}
                {isDueToday && <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.62rem' }}>Due Today</span>}
              </div>
              <div className="d-flex align-items-center gap-1">
                <FaUser size={12} className="text-secondary" />
                <span>Assigned: <strong className="text-dark">{getAssigneeDisplayName(item.assigned_to)}</strong></span>
              </div>
            </div>
          </div>
        </div>

        {/* Drawer Body */}
        <div className="activity-drawer-body">
          {/* New Activity / Handshake Input Card */}
          <div className="activity-post-card shadow-sm">
            <form onSubmit={handleSubmitActivity}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label htmlFor="activityComment" className="form-label fw-bold small mb-0 text-dark d-flex align-items-center gap-1">
                  <FaPaperPlane size={11} className="text-primary" /> Add Day-to-Day Comments / Task Note
                </label>
                <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                  Posting as: <strong className="text-dark">{userName}</strong>
                </span>
              </div>

              {/* Quick Template Chips */}
              <div className="d-flex align-items-center gap-1 flex-wrap mb-2">
                {QUICK_COMMAND_TEMPLATES.map((tmpl, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="btn btn-sm btn-quick-chip"
                    onClick={() => setCommentText(prev => prev ? `${prev} ${tmpl.text}` : tmpl.text)}
                    title={tmpl.text}
                  >
                    {tmpl.label}
                  </button>
                ))}
              </div>

              {/* Main Comment Textarea */}
              <div className="mb-3">
                <textarea
                  id="activityComment"
                  ref={commentInputRef}
                  className="form-control activity-textarea"
                  rows={3}
                  placeholder="Enter day-to-day command, progress note, or handover instruction..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                />
              </div>

              {/* Status and Handshake Reassignment Row */}
              <div className="row g-2 mb-3">
                <div className="col-12 col-sm-6">
                  <label htmlFor="activityStatusSelect" className="form-label small fw-semibold text-muted mb-1">
                    Update Status:
                  </label>
                  <select
                    id="activityStatusSelect"
                    className="form-select form-select-sm"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((st) => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-sm-6">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label htmlFor="activityAssigneeSelect" className="form-label small fw-semibold text-muted mb-0 d-flex align-items-center gap-1">
                      <FaExchangeAlt size={10} className="text-primary" /> Reassign / Assign To:
                    </label>
                    {userEmail && (
                      <button
                        type="button"
                        className="btn btn-xs btn-link p-0 text-decoration-none"
                        style={{ fontSize: '0.72rem' }}
                        onClick={() => setSelectedAssignee(userEmail)}
                      >
                        Self
                      </button>
                    )}
                  </div>
                  <select
                    id="activityAssigneeSelect"
                    className="form-select form-select-sm"
                    value={selectedAssignee}
                    onChange={(e) => setSelectedAssignee(e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {userEmail && (
                      <option value={userEmail}>
                        ⭐ Assign to Myself ({getAssigneeDisplayName(userEmail)})
                      </option>
                    )}
                    {assigneeOptions.map((email) => (
                      <option key={email} value={email}>
                        {getAssigneeDisplayName(email)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Desktop Form Actions */}
              <div className="activity-desktop-actions align-items-center justify-content-end gap-2 mt-3 pt-2 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm px-3 d-inline-flex align-items-center gap-1"
                  onClick={onClose}
                  disabled={submitting}
                >
                  <FaTimes size={12} />
                  <span>Close</span>
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm px-4 fw-semibold shadow-sm d-flex align-items-center gap-2"
                  disabled={submitting}
                >
                  <FaPaperPlane size={12} className={submitting ? 'fa-spin' : ''} />
                  <span>{submitting ? 'Posting...' : 'Post Update'}</span>
                </button>
              </div>
            </form>
          </div>

          {/* Full Activity History Section */}
          <div className="activity-history-section mt-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                <FaHistory className="text-secondary" />
                <span>Full Activity History</span>
                <span className="badge rounded-pill bg-light text-dark border">
                  {activities.length}
                </span>
              </h6>
              <button
                type="button"
                className="btn btn-sm btn-link text-decoration-none p-0 text-muted d-flex align-items-center gap-1"
                onClick={loadActivities}
                title="Refresh history"
              >
                <FaSyncAlt size={11} className={loading ? 'fa-spin' : ''} /> Refresh
              </button>
            </div>

            {loading ? (
              <div className="text-center py-4 text-muted small">
                <FaSyncAlt className="fa-spin mb-2" size={18} />
                <div>Loading activity history...</div>
              </div>
            ) : activities.length === 0 ? (
              <div className="activity-empty-state text-center py-4 text-muted border rounded-3 bg-light">
                <FaHistory size={24} className="mb-2 text-secondary opacity-50" />
                <div className="fw-medium">No activity history recorded yet</div>
                <div className="small text-muted mt-1">
                  Add comments or task updates above to track day-to-day progress.
                </div>
              </div>
            ) : (
              <div className="activity-timeline">
                {activities.map((act) => {
                  const hasHandshake =
                    act.previous_assigned_to &&
                    act.new_assigned_to &&
                    act.previous_assigned_to !== act.new_assigned_to;
                  const hasStatusChange =
                    act.previous_status &&
                    act.new_status &&
                    act.previous_status !== act.new_status;

                  return (
                    <div key={act.id} className="timeline-item">
                      <div className="timeline-marker">
                        <div
                          className="timeline-avatar"
                          title={`${getAssigneeDisplayName(act.updated_by)} (${act.updated_by})`}
                          style={{ background: getAssigneeAvatarGradient(act.updated_by) }}
                        >
                          {(getAssigneeDisplayName(act.updated_by)[0] || 'U').toUpperCase()}
                        </div>
                      </div>

                      <div className="timeline-content shadow-sm">
                        <div className="timeline-header d-flex align-items-center justify-content-between flex-wrap gap-1">
                          <div className="timeline-author text-dark fw-bold">
                            {getAssigneeDisplayName(act.updated_by)}
                            <span className="text-muted fw-normal ms-1" style={{ fontSize: '0.74rem' }}>
                              ({act.updated_by})
                            </span>
                          </div>
                          <div className="timeline-time text-muted" title={formatTimestamp(act.created_at)}>
                            <FaClock size={10} className="me-1" />
                            {getRelativeTime(act.created_at) || formatTimestamp(act.created_at)}
                          </div>
                        </div>

                        {/* Handshake Badge */}
                        {hasHandshake && (
                          <div className="handshake-pill mt-1 mb-2">
                            <FaExchangeAlt size={11} className="text-primary" />
                            <span>
                              <strong>Reassigned:</strong> from{' '}
                              <span className="text-secondary">{getAssigneeDisplayName(act.previous_assigned_to)}</span> &rarr;{' '}
                              <span className="text-primary fw-bold">{getAssigneeDisplayName(act.new_assigned_to)}</span>
                            </span>
                          </div>
                        )}

                        {/* Status Change Badge */}
                        {hasStatusChange && (
                          <div className="status-change-pill mt-1 mb-2">
                            <span>Status changed:</span>
                            <span className="text-muted">{act.previous_status}</span>
                            <span>&rarr;</span>
                            {renderStatusBadge(act.new_status)}
                          </div>
                        )}

                        {/* Comment Body */}
                        {act.comment && (
                          <div className="timeline-body mt-1 text-dark">
                            {act.comment}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Sticky/Fixed Bottom Action Bar (Mobile only < 768px for thumb-reach accessibility) */}
        <footer className="activity-bottom-bar" aria-label="Activity Tracking Mobile Bottom Controls">
          <button
            type="button"
            className="btn btn-outline-secondary activity-bar-btn activity-bar-close-btn d-inline-flex align-items-center justify-content-center gap-1 shadow-sm"
            onClick={onClose}
            aria-label="Close activity tracking drawer"
          >
            <FaTimes />
            <span>Close</span>
          </button>
          <button
            type="button"
            className="btn btn-primary activity-bar-btn activity-bar-submit-btn d-inline-flex align-items-center justify-content-center gap-2 shadow-sm fw-semibold"
            onClick={handleSubmitActivity}
            disabled={submitting}
          >
            <FaPaperPlane size={12} className={submitting ? 'fa-spin' : ''} />
            <span>{submitting ? 'Posting...' : 'Post Update'}</span>
          </button>
        </footer>
      </div>
    </>
  );
};

export default ActivityTrackingDrawer;
